import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { io, Socket } from 'socket.io-client'

import { Biological, Consultation } from '@/core/models/consultation.model'

import { useRequestHandler } from './useRequestHandler'
import { selectConsultationById, selectConsultationUpdateRequestTracker, sendBiological, sendMesures } from '@/redux/reducers/consultation.reducer'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { addDocument, selectDocumentCreateRequestTracker } from '@/redux/reducers/document.reducer'
import { isArray } from 'lodash'
import { Buffer } from 'buffer'
import _ from 'lodash'
import { useSendMesuresSocketMutation } from '@/core/services/consultation'

// const socket = io('http://localhost:4000', {
//   extraHeaders: {
//     'Access-Control-Allow-Private-Network': 'true'
//   }
// })

export default function useMedconnectDevices(isStreamer: boolean, consultationId: string) {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const socket = useRef<Socket>()

  const [deviceTracker, setDeviceTracker] = useState<boolean>(false)
  const [status, setPC300Status] = useState<boolean>(false)
  const biologicals: any[] = []   // prevents the same biological from being entered again in case the device is reconnected
  const [sendMesuresSocket, { isLoading: isSendingMesures }] = useSendMesuresSocketMutation()

  const { dispatch: uploadDocumentDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
      setDeviceTracker(false)
    },
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const { dispatch: sendBiologicalDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.localConsultation.biological.create.success'), { variant: 'success' })
    },
    onError: () => enqueueSnackbar(t('notification.localConsultation.biological.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const sendBiologicalFunction = useCallback(
    (item: Biological) => {
      if (!consultationId) return
      sendBiologicalDispatch(
        sendBiological(consultationId, {
          ...item,
          comment: '',
          files: []
        })
      )
      biologicals.push({ ...item })
      console.log("biological created successfully")
    },
    [consultationId, sendBiologicalDispatch]
  )

  const sendFileFunction = useCallback(
    (response: string[]) => {
      if (!consultationId) return
      if (!isArray(response)) {
        const byteCharacters = Buffer.from(response, 'base64')
        const fileObject = new File([byteCharacters], `ecg-${Date.now().toString()}.pdf`, { type: 'application/pdf' })
        uploadDocumentDispatch(addDocument({ documentFile: fileObject, objectType: 'consultation', objectId: consultationId }))
      } else {
        // convert base64 to blob
        const byteCharacters = Buffer.from(response[0], 'base64')
        const getTypeFromFileSuffix = (fileSuffix: string) => {
          switch (fileSuffix.toLowerCase()) {
            case 'pdf':
              return 'application/pdf'
            case 'jpg':
              return 'image/jpg'
            case 'jpeg':
              return 'image/jpeg'
            case 'png':
              return 'image/png'
            case 'mp4':
              return 'video/mp4'
            default:
              return 'application/pdf'
          }
        }
        const fileObject = new File([byteCharacters], `${response[1]}-${Date.now().toString()}.${response[2] ?? 'pdf'}`, { type: getTypeFromFileSuffix(response[2] ?? 'pdf') })
        uploadDocumentDispatch(addDocument({ documentFile: fileObject, objectType: 'consultation', objectId: consultationId }))
      }
    },
    [consultationId, uploadDocumentDispatch]
  )

  useEffect(() => {
    socket.current = io('http://localhost:4000')

    return () => {
      socket.current?.close()
    }
  }, [])

  const isBiologicalExisting = (item: Biological) => {
    return biologicals.some(biological =>
      biological.label === item.label &&
      biological.unit === item.unit &&
      biological.value === item.value
    );
  }

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data.type === 'PC300') {
        console.log('received PC300', event.data.PC300)
        dispatch(sendMesures(consultationId ? consultationId : '', [event.data.PC300]))
      } else if (event.data.type === 'biological') {
        console.log('received biological', event.data.biological)
        if (!isBiologicalExisting(event.data.biological)) {
          console.log('biological not existing yet')
          sendBiologicalFunction(event.data.biological)
        } else {
          console.log('Biological already existing !!!!!')
        }
      }
    }

    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
    }
  }, [dispatch, consultationId])



  useEffect(() => {
    if (!isStreamer || !socket.current) return

    //The ref value 'socket.current' will likely have changed by the time this effect cleanup function runs.
    //If this ref points to a node rendered by React, copy 'socket.current' to a variable inside the effect,
    //and use that variable in the cleanup function  react-hooks/exhaustive-deps

    socket.current.on('PC300', (mesures: Consultation['mesures']) => {

      if (consultationId) sendMesuresSocket({ consultationId, mesures })

    })

    socket.current.on('PC300status', (status: boolean) => setPC300Status(status))

    socket.current.on('biological', sendBiologicalFunction)

    socket.current.on('file', sendFileFunction)

    return () => {
      if (!socket.current) return
      socket.current.off('biological')
      socket.current.off('file')
      socket.current.off('PC300')
      socket.current.off('PC300status')
    }
  }, [consultationId, dispatch, isStreamer, sendBiologicalFunction, sendFileFunction])

  return {
    setDeviceTracker,
    deviceTracker,
    status,
  }
}
